import { useState, useEffect, useCallback } from 'react';

const CACHE_KEY = 'google_fonts_cache';
const CACHE_EXPIRY_DAYS = 7;
const GOOGLE_FONTS_API = 'https://www.googleapis.com/webfonts/v1/webfonts';

// Get API key from environment variable or use null
const getApiKey = () => {
  return process.env.REACT_APP_GOOGLE_FONTS_API_KEY || null;
};

// Helper to normalize font weights from variants
const normalizeWeights = (variants) => {
  const weightMap = {
    '100': '100',
    '200': '200',
    '300': '300',
    'regular': '400',
    '400': '400',
    '500': '500',
    '600': '600',
    '700': '700',
    '800': '800',
    '900': '900',
  };

  const weights = new Set();
  
  variants.forEach(variant => {
    // Remove 'italic' suffix if present
    const baseVariant = variant.replace('italic', '').trim();
    if (weightMap[baseVariant]) {
      weights.add(weightMap[baseVariant]);
    }
  });

  // Convert to sorted array
  return Array.from(weights).sort((a, b) => parseInt(a) - parseInt(b));
};

// Fetch fonts from Google Fonts API
// We only fetch metadata (names and weights), not the actual font files
const fetchFontsFromAPI = async (apiKey = null) => {
  const key = apiKey || getApiKey();
  
  if (!key) {
    throw new Error('Google Fonts API key is required. Please set REACT_APP_GOOGLE_FONTS_API_KEY in your .env file');
  }
  
  const url = `${GOOGLE_FONTS_API}?key=${key}`;
  
  try {
    const response = await fetch(url);
    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Invalid API key or access denied');
      }
      if (response.status === 429) {
        throw new Error('API rate limit exceeded. Using cached fonts if available.');
      }
      throw new Error(`Failed to fetch fonts: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    
    if (!data.items || !Array.isArray(data.items)) {
      throw new Error('Invalid response format from Google Fonts API');
    }
    
    // Extract only what we need: family names and weights (variants)
    // We don't download or use the actual font files
    const fonts = data.items.map(font => ({
      family: font.family, // Font name
      weights: normalizeWeights(font.variants || []), // Available weights
      category: font.category || '', // Optional: serif, sans-serif, etc.
    }));
    
    return fonts;
  } catch (error) {
    console.error('Error fetching Google Fonts:', error);
    throw error;
  }
};

// Get cached fonts
const getCachedFonts = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return null;
    
    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();
    const expiryTime = CACHE_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    // Check if cache is expired
    if (now - timestamp > expiryTime) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    
    return data;
  } catch (error) {
    console.error('Error reading font cache:', error);
    return null;
  }
};

// Save fonts to cache
const saveFontsToCache = (fonts) => {
  try {
    const cacheData = {
      data: fonts,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving font cache:', error);
    // If storage is full, try to clear old cache
    if (error.name === 'QuotaExceededError') {
      localStorage.removeItem(CACHE_KEY);
    }
  }
};

export const useGoogleFonts = (apiKeyOverride = null) => {
  const [fonts, setFonts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadFonts = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    setError(null);

    try {
      // Check cache first (unless forcing refresh)
      if (!forceRefresh) {
        const cachedFonts = getCachedFonts();
        if (cachedFonts && cachedFonts.length > 0) {
          setFonts(cachedFonts);
          setLoading(false);
          return;
        }
      }

      // Fetch from API (only metadata - names and weights, not font files)
      const fetchedFonts = await fetchFontsFromAPI(apiKeyOverride);
      
      // Save to cache
      saveFontsToCache(fetchedFonts);
      
      // Update state
      setFonts(fetchedFonts);
    } catch (err) {
      setError(err.message);
      
      // Fallback to cache if API fails
      const cachedFonts = getCachedFonts();
      if (cachedFonts && cachedFonts.length > 0) {
        setFonts(cachedFonts);
        setError('Using cached fonts. API unavailable: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [apiKeyOverride]);

  useEffect(() => {
    loadFonts();
  }, [loadFonts]);

  return {
    fonts,
    loading,
    error,
    refreshFonts: () => loadFonts(true)
  };
};

